<template>
  <div id="cms-tab-component" ref="cmsTabComponent" class="tabs-content-container" :class="containerClasses">
    <slot v-bind="{ui, activeTab, productGroups, updateSelectedTab}"></slot>
  </div>
</template>
<script>
import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';
import {updateStockWatchStatus} from '@/components/containers/bulk-product/utils/warehouses';
import ProductGroups from '@/components/containers/bulk-product/utils/product-groups';
import {ProductApi} from '@/api/product';
import findIndex from 'lodash/findIndex';
import {getElementHeight, scrollToElement} from '@/utils/scroll-to';
import {useIntersectionObserver} from '@/composables/intersection-observer';

export default {
  name: 'TabsContentContainer',
  props: {
    initialTab: {
      type: String,
      default: '',
    },
    baseProductCode: {
      type: String,
      required: true,
    },
  },
  setup() {
    const {intersectionObserver, isElementVisible: areTabsInView} = useIntersectionObserver();
    return {intersectionObserver, areTabsInView};
  },
  data() {
    return {
      collapseAnimationDelay: 600,
      collapseAnimationOffsetDelay: 675,
      productGroups: [],
      productStatus: {},
      initialTabSet: false,
      mobile: {
        container: 'pdl-collapse',
        item: 'pdl-collapse-item',
        labelKey: 'title',
        options: {
          accordion: true,
          defaultActive: false,
          iconActive: 'remove',
          iconInactive: 'add',
        },
      },
      desktop: {
        container: 'pdl-tabs',
        item: 'pdl-tab-pane',
        labelKey: 'label',
        options: {
          isSticky: true,
          isLazy: false,
          alignment: 'center',
          tabPosition: 'top',
        },
      },
    };
  },
  componentName: 'TabsContentContainer',
  computed: {
    ...mapState('viewSize', ['viewBreakpoint']),
    ...mapState('backend', ['unitId', 'b2b', 'isElectraBrand']),
    ...mapState('global', ['headerHeight']),
    ...mapGetters('storage', ['purchaseMode']),
    ...mapGetters('productDetails', ['getActiveTab']),
    ...mapGetters('viewSize', ['viewBreakpointLessThan']),

    activeTab: {
      get() {
        return this.getActiveTab;
      },
      set(value) {
        this.setActiveTab(value);
        if (this.initialTabSet && this.areTabsInView) {
          this.intersectionObserver.disconnect();
          this.scrollToContent();
        }
      },
    },
    ui() {
      if (this.b2b) {
        return this.viewBreakpointLessThan('large') ? this.mobile : this.desktop;
      }
      return this.desktop;
    },
    containerClasses() {
      return {'is-electra': this.isElectraBrand, 'px-2 lg:px-0': this.b2b, 'mt-2': this.purchaseMode};
    },
  },
  created() {
    this.activeTab = this.initialTab;
    this.initialTabSet = true;
  },
  mounted() {
    if (this.unitId !== 'b2c') {
      this.fetchProducts();
    }

    this.$nextTick(() => {
      this.setTabHeaderHeight(getElementHeight('.pdl-tabs__header'));
      this.intersectionObserver.observe(this.$refs.cmsTabComponent);
    });
  },
  methods: {
    ...mapMutations('productDetails', ['setActiveTab', 'setTabHeaderHeight']),
    ...mapMutations('storage', ['setBikeTechSKU']),
    ...mapActions('skuGrid', ['loadAllocatedCounts']),
    scrollToContent() {
      if (this.b2b && this.viewBreakpointLessThan('large')) {
        setTimeout(() => {
          let primaryNavElement = document.getElementById('abbreviatedNavigationBar');
          const stickyNavOffset = primaryNavElement.getBoundingClientRect().height;
          let elements = document.getElementsByName(this.activeTab);
          elements?.[0]?.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
          setTimeout(() => {
            window.scrollBy({top: -stickyNavOffset, behavior: 'smooth'});
          }, this.collapseAnimationOffsetDelay);
        }, this.collapseAnimationDelay);
      } else {
        scrollToElement('#cms-tab-component', this.headerHeight);
      }
    },
    /**
     * Get product's ID from current URL.
     * @return {string|*}
     */
    getProductIdFromUrl() {
      const pathArray = window.location.pathname.split('/');
      const index = findIndex(pathArray, (x) => x === 'p');

      return index > -1 && pathArray.length > index + 1 ? pathArray[index + 1] : '';
    },

    /**
     * Update stock data.
     * @param payload
     */
    updateStocks(payload) {
      if (payload && payload.item) {
        updateStockWatchStatus(this.productGroups, payload.item);
      }
    },

    /**
     * Fetch product data.
     */
    fetchProducts() {
      const productId = this.getProductIdFromUrl();
      this.setBikeTechSKU(productId);

      if (this.baseProductCode) {
        ProductApi.loadProduct(this.baseProductCode, this.b2b).then((data) => {
          const productGroups = new ProductGroups(data);
          this.productGroups = productGroups.getProductGroups();
          this.productStatus = data.status;
        });

        this.loadAllocatedCounts([this.baseProductCode]);
      }
    },
    updateSelectedTab(tab) {
      this.activeTab = tab;
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs-content-container {
  &.is-electra {
    :deep(.pdl-tabs) {
      .pdl-tabs__header.is-sticky {
        background-color: var(--electra-light-blue);

        .form-group {
          .select {
            background-color: theme('colors.white');

            & select {
              background-color: transparent;
              color: theme('textColor.black');
            }

            &::after {
              color: theme('textColor.black');
            }
          }
        }

        .pdl-tabs__item {
          &:hover,
          &.is-active {
            color: theme('colors.black');
          }

          color: var(--electra-gray);
        }

        .pdl-tabs__active-bar {
          background-color: theme('colors.black');
        }
      }
    }
  }
}

:deep(.pdl-tabs) {
  .pdl-tabs__header {
    &.is-sticky {
      top: var(--header-height);
      padding: theme('spacing.8') theme('spacing.4');
      background-color: theme('colors.black');
      z-index: 10;

      .pdl-tabs__item {
        font-family: theme('fontFamily.heading');
        font-weight: theme('fontWeight.bold');
        font-size: theme('fontSize.xl');
        height: 2.5rem;
        line-height: 2.5rem;

        &:hover,
        &.is-active {
          color: theme('colors.white');
        }
      }

      .pdl-tabs__active-bar {
        background-color: theme('colors.white');
      }

      .pdl-tabs__nav-wrap::after {
        background-color: transparent;
      }
    }
  }
}
</style>
